import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo36() {
  const pdfRef = useRef();

  // 📊 Aerospace Employment Data
  const aerospaceData = [
    { year: 2018, employment: 7668 },
    { year: 2019, employment: 8054 },
    { year: 2020, employment: 8346 },
    { year: 2021, employment: 10629 },
    { year: 2022, employment: 11887 },
    { year: 2023, employment: 13732 },
    { year: 2024, employment: 14059 },
  ];

  // 🧮 Change Calculation
  const change = (
    ((aerospaceData[aerospaceData.length - 1].employment -
      aerospaceData[0].employment) /
      aerospaceData[0].employment) *
    100
  ).toFixed(2);

  const trend = change > 0 ? "increased" : "decreased";

  // 🎨 Year-specific Colors
  const colors = [
    "#FFF085", // 2018
    "#F6CFFF", // 2019
    "#2C92B8", // 2020
    "#9F9FA9", // 2021
    "#E9D4FF", // 2022
    "#D8F999", // 2023
    "#82181A", // 2024
  ];

  // 📈 Chart
  const chartData = {
    labels: aerospaceData.map((d) => d.year.toString()),
    datasets: [
      {
        label: "Employment Count",
        data: aerospaceData.map((d) => d.employment),
        backgroundColor: colors,
        borderColor: "#333",
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "🚀 Aerospace Product & Parts Manufacturing Employment (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.raw.toLocaleString("en-US")} employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#4B5563", callback: (v) => v.toLocaleString("en-US") },
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
          color: "#111827",
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Year",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 📄 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("aerospace_employment_2018_2024.pdf");
      // input.style.width = originalWidth;
    });
  };

  // 🧩 Render
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Aerospace Product & Parts Manufacturing Employment (2018–2024)
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Utah’s aerospace manufacturing sector shows strong workforce growth
          since 2018, reflecting rapid innovation and defense industry
          expansion.
        </p>

        {/* Insight Card */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6 text-center">
          <div
            className={`p-4 rounded-xl shadow ${
              change > 0 ? "bg-green-100" : "bg-red-100"
            }`}
          >
            <p className="text-sm text-gray-600 font-medium">
              Employment Change (2018–2024)
            </p>
            <p
              className={`text-lg font-bold ${
                change > 0 ? "text-green-700" : "text-red-700"
              }`}
            >
              {change > 0 ? "+" : ""}
              {change}% ({trend})
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2 text-left">Year</th>
                  <th className="p-2" style={{ textAlign: 'right' }}>Employment Count</th>
                </tr>
              </thead>
              <tbody>
                {aerospaceData.map((row, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-100 transition bg-white even:bg-gray-50"
                  >
                    <td className="p-2 font-medium text-left">{row.year}</td>
                    <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.employment.toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
            <div className="x-axis-legend" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9fafb', borderLeft: '4px solid #014cb2', fontSize: '14px', color: '#6b7280', marginLeft: '16px', marginRight: '16px', marginBottom: '16px' }}>
              <strong>X-axis Legend:</strong> The chart displays years (2018 through 2024) on the x-axis, with each bar representing the employment count for the Aerospace Product and Parts Manufacturing sector in that specific year.
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          🚀 From 2018 to 2024, aerospace employment in Utah{" "}
          <span
            className={`font-semibold ${
              change > 0 ? "text-green-700" : "text-red-700"
            }`}
          >
            {trend} by {Math.abs(change)}%
          </span>
          , signaling continuous growth in Utah’s advanced manufacturing sector.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
